import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@mui/material";
import { format, parseJSON } from "date-fns";
import CancelledIcon from "@icons/myicons/cancelled.svg?react";
import { Booking, InvestmentTypeEnum } from "@rentablo/api/apiv1";
import {
  getValueByInvestmentAndBookingType,
  toCurrency,
  toGermanNumberFormat,
} from "@helpers";
import { InvestmentInfo } from "@components";
import { BookingType } from "@components";
import { TableConfig } from "@rentablo/components";
import { GainLossTypography } from "@rentablo/components";
import { Tooltip } from "@rentablo/components";
import { ValueDateWrapper } from "@features/investments/investment/components/BookingsSingleInvestmentTable";
import { TransactionStatusIcon } from "@features/investments/investment/components/TransactionStatusIcon";

export type BookingsTableAdditionalProps = {
  logos: Record<string, string | null | undefined>;
};

type RendererProps = {
  row: Booking;
} & Partial<BookingsTableAdditionalProps>;

const InvestmentTypeCell = ({ row }: RendererProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        <BookingType type={row.type} />
        {row.cancellationId != undefined && (
          <Tooltip text={t("investment.protocolTable.cancelled")}>
            <Box display="flex">
              <CancelledIcon
                style={{
                  width: "16px",
                  height: "16px",
                  color: theme.palette.text.secondary,
                  cursor: "help",
                }}
              />
            </Box>
          </Tooltip>
        )}
      </Box>
      <ValueDateWrapper>
        <TransactionStatusIcon
          dataConsistencyStatus={row.dataConsistencyStatus}
          dataSource={row.dataSource}
          isExternalID={Boolean(row.externalId)}
        />
        <Typography variant="body3" color="textSecondary" ml={2}>
          {format(parseJSON(row.valueDate), "dd.MM.yyyy")}
        </Typography>
      </ValueDateWrapper>
    </>
  );
};

const NameCell = ({ row, logos = {} }: RendererProps) => (
  <InvestmentInfo
    item={row.investment}
    image={
      logos[row.investment.isin || ""] ||
      logos[row.investment?.tickerSymbol || ""]
    }
  />
);

const PriceCell = ({ row }: RendererProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="body3" color="textPrimary">
        {getValueByInvestmentAndBookingType({
          value: Math.abs(row.securityPrice),
          currency: row.currency,
          investmentType: row.investment.type as InvestmentTypeEnum,
          bookingType: row.type,
          percentageRange: "0-100",
          quoteNotation: row.investment.quoteNotation,
        })}
      </Typography>
      <Typography variant="caption" fontWeight={600} color="textSecondary">
        {toGermanNumberFormat(row.numberOfLots, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        })}{" "}
        {t("components.depotsStatistic.bookingsTable.lots")}
      </Typography>
    </>
  );
};

const AmountCell = ({ row }: RendererProps) => (
  <GainLossTypography
    value={row.amount}
    justifyContent="end"
    variant="body1"
    fontWeight={600}
  >
    {row.amount ? toCurrency(row.amount, row.currency) : "N/A"}
  </GainLossTypography>
);

const INVESTMENTS_BOOKINGS_TABLE_KEY = "investmentsBookingsTable";

type SortArgs = {
  sortField?: string;
  sortDirection?: TableSortDirection;
  onSort: (field: string) => void;
};

export const useBookingsTableConfig = ({
  sortField,
  sortDirection,
  onSort,
}: SortArgs): TableConfig<Booking> => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      id: "depots-bookings-table",
      sorting:
        sortField && sortDirection
          ? {
              field: sortField,
              direction: sortDirection,
              saveKey: INVESTMENTS_BOOKINGS_TABLE_KEY,
            }
          : undefined,
      onSort,
      getRowId: (row) => row.id,
      columns: [
        {
          id: "name",
          title: t("components.depotsStatistic.bookingsTable.headers.name"),
          renderer: NameCell,
        },
        {
          id: "valueDate",
          sortKey: "valueDate",
          title: t("components.depotsStatistic.bookingsTable.headers.type"),
          sortable: true,
          renderer: InvestmentTypeCell,
        },
        {
          id: "securityPrice",
          title: t("components.depotsStatistic.bookingsTable.headers.price"),
          renderer: PriceCell,
        },
        {
          id: "amount",
          sortKey: "amount",
          title: t("components.depotsStatistic.bookingsTable.headers.amount"),
          sortable: true,
          align: "right",
          renderer: AmountCell,
        },
      ],
    }),
    [t, sortField, sortDirection, onSort]
  );
};
